// FAR-76: the Months 4–18 tab's calculations. Pure, so the rules the spec
// states -- which months, which SKUs count, the colour bands, the totals -- are
// tested without a browser.

// FAR-83 #14: the tab's months, as slice indexes into the plan's months.
export const FAR_FIRST = 3
export const FAR_LAST = 18

// §1: months 4–18 of the plan, i.e. everything after the first three, up to 15.
export function horizonMonths(months) {
  return (months || []).slice(FAR_FIRST, FAR_LAST)
}

// §3 utilization bands (Open Item 1: "confirm"; constants, so easy to change).
// FAR-88 S5 / S16 (FAR-90 S5): the mockup's band fills, with one dark neutral
// text colour on every band (not green).
const CHIP_TEXT = '#1F2A1F'
export const UTIL_BANDS = [
  { max: 80, key: 'under', label: 'Under 80%', color: '#DDEFD8', text: CHIP_TEXT },
  { max: 90, key: 'good', label: '80–90%', color: '#B9DDB0', text: CHIP_TEXT },
  { max: 100, key: 'caution', label: '90–100%', color: '#F5E2A0', text: CHIP_TEXT },
  { max: Infinity, key: 'over', label: 'Over 100%', color: '#EBB4AA', text: CHIP_TEXT },
]

export function utilBand(pct) {
  if (pct == null || Number.isNaN(pct)) return null
  return UTIL_BANDS.find((b) => pct < b.max) || UTIL_BANDS[UTIL_BANDS.length - 1]
}

// FAR-84 §3.3 / FAR-94: a line's load in a month. A SKU is a run when it has
// units that month and a run rate; its run hours are units ÷ run rate on its
// PRIMARY line (the row's `_hrs`). A SKU with no run rate adds nothing (FAR-90
// U1). Each run adds the line's changeover per run.
export function lineLoad(rows, lineName, monthKey, changeoverHoursPerRun) {
  let runHours = 0
  let runs = 0
  for (const r of rows || []) {
    if (r.line !== lineName || !(r.runRateCasesPerHour > 0) || !((r[`${monthKey}_cases`] || 0) > 0)) continue
    runHours += r[`${monthKey}_hrs`] || 0
    runs += 1
  }
  const changeover = runs * (changeoverHoursPerRun || 0)
  return { runHours, runs, changeover, total: runHours + changeover }
}

// §3: a line's utilization in a month = (run hours + changeover per run × runs)
// ÷ H. H (grossHours) is served by GET /capacity/lines -- working days × hours
// per day, computed once in Go (§11) -- and not recomputed here. null when the
// line has no hours, so the card shows "—" instead of an infinite percentage.
export function lineUtilization(rows, lineName, monthKey, capacityLine, grossHours) {
  if (!capacityLine || !(grossHours > 0)) return null
  return (lineLoad(rows, lineName, monthKey, capacityLine.changeoverHoursPerRun).total / grossHours) * 100
}

// FAR-83 §3 / FAR-84 §3.3: Overall Capacity -- all lines together:
// Σ lines (run + changeover) ÷ Σ lines H. grossHoursByLine is line → H for
// the month. null when no line has hours, so "—" shows.
export function overallUtilization(rows, monthKey, capacityLines, grossHoursByLine) {
  let load = 0
  let avail = 0
  for (const l of capacityLines || []) {
    const h = grossHoursByLine?.[l.line]
    if (h > 0) avail += h
    load += lineLoad(rows, l.line, monthKey, l.changeoverHoursPerRun).total
  }
  if (avail <= 0) return null
  return (load / avail) * 100
}

// H for a month across the lines: line → hours, from the served grossHours
// (line → month → hours).
export function grossHoursForMonth(grossHours, monthIso) {
  return Object.fromEntries(Object.entries(grossHours || {}).map(([line, byMonth]) => [line, byMonth?.[monthIso]]))
}

// §3: % of SKUs *produced that month* (units > 0) whose month-end DOS is below
// Min / above Max. SKUs with no DOS band are excluded (§5), so made-to-order
// items (no band) are too. null when nothing is produced.
// FAR-88 / FAR-90 U1: SKUs with no run rate are left out too, even when a
// typed edit gives them units.
export function dosRiskPct(rows, monthKey, which) {
  const produced = (rows || []).filter((r) =>
    (r[`${monthKey}_cases`] || 0) > 0 && hasBand(r) && hasRunRate(r)
    && r[`${monthKey}_dos`] != null && !r[`${monthKey}_noDemand`])
  if (produced.length === 0) return null
  const hit = produced.filter((r) => {
    const dos = r[`${monthKey}_dos`]
    return which === 'below' ? dos < r.dosMin : dos > r.dosMax
  })
  return (hit.length / produced.length) * 100
}

// FAR-88 / FAR-90 U1: a SKU with no Run Rate Table row has rate 0 on the
// board (boardRows.js); it adds no hours and is outside the risk rows.
export function hasRunRate(r) {
  return (r?.runRateCasesPerHour || 0) > 0
}

// FAR-88 / FAR-90 C6: how many SKUs the capacity figures leave out.
export function noRunRateCount(rows) {
  return (rows || []).filter((r) => !hasRunRate(r)).length
}

// FAR-88 / FAR-90 S6: a risk bar's fill, as a % of its track: 15% fills it.
export const RISK_BAR_FULL = 15
export function riskBarFill(pct) {
  if (!(pct > 0)) return 0
  return Math.min(100, (pct / RISK_BAR_FULL) * 100)
}

export function hasBand(r) {
  return (r?.dosMin || 0) > 0 && (r?.dosMax || 0) > 0
}

// §4 DOS cell colouring: red below Min, amber above Max, none in band or unbanded.
export function dosStatus(r, dos, monthKey) {
  if (dos == null || !hasBand(r)) return 'none'
  // FAR-84 §4.1: DOS 0 with no demand ahead isn't a shortage.
  if (monthKey && r?.[`${monthKey}_noDemand`]) return 'ok'
  if (dos < r.dosMin) return 'below'
  if (dos > r.dosMax) return 'above'
  return 'ok'
}

// §4 filters: primary line (or all) AND status (all / below / above). A SKU
// "is below Min" if any month in the horizon is.
export function filterRows(rows, months, { line = '', status = 'all' } = {}) {
  return (rows || []).filter((r) => {
    if (line && r.line !== line) return false
    if (status === 'all') return true
    return (months || []).some((m) => dosStatus(r, r[`${m.key}_dos`], m.key) === status)
  })
}

// §4 footer totals: over ALL SKUs (ignoring filters), in CASES. Pallet items
// can't be added to cases without a cases-per-pallet factor we don't have
// (Open Item 3), so they're excluded and counted.
export function footerTotals(rows, months, baseRowsByItem) {
  const cases = (rows || []).filter((r) => (r.baseUom || 'CS') === 'CS')
  const excluded = (rows || []).length - cases.length
  const perMonth = {}
  let cumUnits = 0
  let cumDemand = 0
  for (const m of months || []) {
    let units = 0
    let demand = 0
    let inventory = 0 // FAR-77 footer; 4–18 doesn't show it
    let base = 0
    let dosSum = 0
    let dosN = 0
    let baseKnown = true
    for (const r of cases) {
      units += r[`${m.key}_cases`] || 0
      demand += r[`${m.key}_fcst`] || 0
      inventory += r[`${m.key}_endInv`] || 0 // made to order counts (FAR-83: typed surplus is real stock)
      const dos = r[`${m.key}_dos`]
      // No demand ahead: DOS 0 by rule, not a level to average in.
      if (dos != null && hasBand(r) && !r[`${m.key}_noDemand`]) { dosSum += dos; dosN += 1 }
      const b = baseRowsByItem?.[r.itemNumber]
      if (b) base += b[`${m.key}_cases`] || 0
      else baseKnown = false
    }
    cumUnits += units
    cumDemand += demand
    perMonth[m.key] = {
      units,
      demand,
      inventory,
      avgDos: dosN ? dosSum / dosN : null,
      cumPlanVsDemand: cumUnits - cumDemand,
      cumPlanVsDemandPct: cumDemand ? ((cumUnits - cumDemand) / cumDemand) * 100 : null,
      deltaVsBase: baseRowsByItem && baseKnown ? units - base : null,
      deltaVsBasePct: baseRowsByItem && baseKnown && base ? ((units - base) / base) * 100 : null,
    }
  }
  return { perMonth, excludedPallet: excluded }
}

// ── FAR-76 §4: editing Units ────────────────────────────────────────────────
// A planner's typed Units are one "Cell" modification per SKU (scope
// ItemNumber, monthValues {iso: units}). The engine applies it last, as an
// absolute value.

export const CELL = 'Cell'

// The SKU's Cell modification, if any. Ignored ones count as "no edits".
export function cellModFor(modifications, itemNumber) {
  return (modifications || []).find(
    (m) => m.changeType === CELL && !m.isIgnored && String(m.identifier) === String(itemNumber),
  ) || null
}

// Parse what was typed: "4,800" -> 4800. null when it isn't a number >= 0.
export function parseUnits(text) {
  const s = String(text ?? '').replace(/[,\s]/g, '')
  if (s === '') return null
  const n = Number(s)
  return Number.isFinite(n) && n >= 0 ? Math.round(n) : null
}

// Open Item 5 (Tyler, 2026-10-17): save what the planner types; warn when it
// isn't a multiple of the item's lot size. Returns the lot size, or null.
export function lotWarning(units, roundingValue) {
  if (units == null || !roundingValue || roundingValue <= 0) return null
  return units % roundingValue === 0 ? null : roundingValue
}

// The SKU's monthValues after typing `units` into month `iso`. Typing the
// Base Plan's value removes that month's edit. Returns {} when no edits remain
// (the caller deletes the modification).
export function nextCellValues(existing, iso, units, baseUnits) {
  const next = { ...(existing?.monthValues || {}) }
  if (baseUnits != null && Math.round(baseUnits) === units) delete next[iso]
  else next[iso] = units
  return next
}

// ── FAR-76 §5: Apply Modification ───────────────────────────────────────────
// Checked SKUs, a percent (0–200, step 10, default 100) of a basis DOS (Min /
// Target / Max, default Min). Saved as one "DOSTarget" rule; the engine works
// out each month's units as inventory chains.

export const DOS_TARGET = 'DOSTarget'
export const BASES = ['Min', 'Target', 'Max']
export const PCT_STEPS = Array.from({ length: 21 }, (_, i) => i * 10)

// Open Item 7: only checked rows that are visible under the current filters.
export function checkedVisible(rows, months, filters, selected) {
  return filterRows(rows, months, filters)
    .filter((r) => selected?.has(r.itemNumber))
    .map((r) => r.itemNumber)
}

// "100% of Min DOS on 3 SKUs"
export function dosTargetName(pct, basis, count) {
  return `${pct}% of ${basis} DOS on ${count} SKU${count === 1 ? '' : 's'}`
}

// The rule's body. It covers every horizon month the tab shows.
export function dosTargetMod(items, pct, basis, months) {
  const monthValues = {}
  for (const m of months || []) monthValues[m.iso] = pct
  return {
    name: dosTargetName(pct, basis, items.length),
    scope: 'Items', items: [...items], basis,
    changeType: DOS_TARGET, monthValues,
  }
}
